import {filter} from 'rxjs';
import {useCallback, useEffect, useState} from 'react';

import {
  Conversation,
  ConversationEvent,
} from '@stripe-internal/conversations-client';
import {
  MessageToParent,
  MessageToParentType,
  Visibility,
} from '@stripe-internal/support-conversations-controller';

import type {ParentMessageBus} from '../../../core/lib/parentMessageBus/ParentMessageBus';
import {useDocumentVisibilityForParentMessageBus} from './useDocumentVisibility';
import {useParentMessageBus, useVisibility} from '../../../core/hooks/appHooks';

const widgetNotOpen = (
  visibility: Visibility,
  documentVisibility: DocumentVisibilityState,
): boolean => {
  return visibility !== 'open' || documentVisibility === 'hidden';
};

const shouldIncreaseUnseenMessagesCount = (
  visibility: Visibility,
  documentVisibility: DocumentVisibilityState,
): boolean => {
  return widgetNotOpen(visibility, documentVisibility);
};

const isNonUserMessageEvent = (
  event: ConversationEvent,
  conversation: Conversation,
) => {
  return (
    event.name === 'conversation-message-added' &&
    event.participantId !== conversation.user.id
  );
};

function useSetUnseenMessageCount({post}: ParentMessageBus) {
  return useCallback(
    (count: number) => {
      try {
        const msg: MessageToParent = {
          type: MessageToParentType.SetUnseenMessagesCount,
          data: {unseenMessagesCount: count},
        };
        post(msg);
      } catch {}
    },
    [post],
  );
}

export function useUnseenConversationMessagesForEnvironment({
  conversation,
  parentMessageBus,
  visibility,
}: {
  conversation: Conversation;
  parentMessageBus: ParentMessageBus;
  visibility: Visibility;
}) {
  const documentVisibility =
    useDocumentVisibilityForParentMessageBus(parentMessageBus);

  const setUnseenMessagesCount = useSetUnseenMessageCount(parentMessageBus);
  const [unseenCount, setUnseenCount] = useState<number>(0);

  useEffect(() => {
    setUnseenMessagesCount(unseenCount);
  }, [setUnseenMessagesCount, unseenCount]);

  useEffect(() => {
    if (unseenCount && !widgetNotOpen(visibility, documentVisibility)) {
      setUnseenCount(0);
    }
  }, [unseenCount, visibility, documentVisibility]);

  useEffect(() => {
    const visuallyNotifiableEvents$ = conversation
      .eventsObservable()
      .pipe(filter((event) => isNonUserMessageEvent(event, conversation)));

    const sub = visuallyNotifiableEvents$.subscribe((_) => {
      if (shouldIncreaseUnseenMessagesCount(visibility, documentVisibility)) {
        setUnseenCount((prevCount) => prevCount + 1);
      }
    });

    return () => sub.unsubscribe();
  }, [conversation, documentVisibility, visibility]);
}

export function useUnseenConversationMessages(conversation: Conversation) {
  const {visibility} = useVisibility();
  const parentMessageBus = useParentMessageBus();

  useUnseenConversationMessagesForEnvironment({
    conversation,
    parentMessageBus,
    visibility,
  });
}
